import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Globe, ArrowLeft } from "lucide-react";
import { publicApi } from "@/lib/api";
import { SectionHeading, PropertyCard, Loader, CATEGORY_LABELS, CATEGORY_COPY, ctaForProperty } from "@/components/public/ui";

export default function PropertyDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined);

  useEffect(() => {
    setP(undefined);
    publicApi.get(`/properties/${slug}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [slug]);

  if (p === undefined) return <div className="pt-20"><Loader /></div>;
  if (p === null)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 pt-20">
        <p className="font-serif text-3xl text-[#18344A]">Property not found</p>
        <Link to="/portfolio" className="text-sm font-semibold uppercase tracking-wide text-[#526E7E]">Back to Portfolio</Link>
      </div>
    );

  const comingSoon = p.status === "coming_soon" || p.status === "development";

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="bg-[#18344A] py-16 text-white">
        <div className="mx-auto w-full max-w-[1100px] px-5 lg:px-8">
          <Link to="/portfolio" className="mb-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/70 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All Properties
          </Link>
          {comingSoon && <div className="mb-3"><span className="rounded-full bg-[#B7A58E] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#18344A]">Coming Soon</span></div>}
          <h1 className="font-serif text-[44px] leading-tight text-white sm:text-[64px]">{p.name}</h1>
          <p className="mt-2 flex items-center gap-2 text-sm uppercase tracking-wide text-white/75"><MapPin className="h-4 w-4" /> {p.city}, {p.state}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(p.categories || []).map((c) => (
              <span key={c} className="rounded-full border border-white/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/90">{CATEGORY_LABELS[c]}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Feature image */}
      {p.image && (
        <section className="mx-auto max-w-[1100px] px-5 pt-16 lg:px-8">
          <div className="overflow-hidden rounded-2xl">
            <img src={p.image} alt={p.name} className="aspect-[21/9] w-full object-cover" />
          </div>
        </section>
      )}

      {/* Intro + contact */}
      <section className="mx-auto max-w-[1100px] px-5 py-20 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading eyebrow="Introduction" title={p.tagline || "A Pack Brothers Destination"} />
            <p className="mt-5 text-[16px] leading-relaxed text-[rgba(48,56,60,0.8)]">{p.description}</p>

            <h3 className="mt-12 font-serif text-2xl text-[#18344A]">Experience Highlights</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {(p.categories || []).map((c) => (
                <div key={c} className="rounded-xl border border-[var(--pb-border)] bg-white p-5">
                  <div className="font-serif text-xl text-[#18344A]">{CATEGORY_LABELS[c]}</div>
                  <p className="mt-1 text-sm text-[rgba(48,56,60,0.7)]">{CATEGORY_COPY[c]}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="h-fit rounded-2xl border border-[var(--pb-border)] bg-[#F5F3EF] p-6">
            <h3 className="font-serif text-2xl text-[#18344A]">Visit</h3>
            <div className="mt-4 space-y-3 text-sm text-[rgba(48,56,60,0.8)]">
              {p.address && <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-[#526E7E]" /> {p.address}</p>}
              {p.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#526E7E]" /> {p.phone}</p>}
              {p.website && <p className="flex items-center gap-2"><Globe className="h-4 w-4 text-[#526E7E]" /> <a href={p.website} target="_blank" rel="noreferrer" className="font-medium text-[#18344A] underline-offset-2 hover:underline">Visit Website</a></p>}
            </div>
            {p.website ? (
              <a href={p.website} target="_blank" rel="noreferrer" data-testid="property-website-cta" className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#18344A] px-6 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-white hover:bg-[#18344A]/90">
                {ctaForProperty(p)} <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <Link to="/contact" className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#18344A] px-6 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-white hover:bg-[#18344A]/90">
                Get Updates <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </aside>
        </div>
      </section>

      {/* Related */}
      {p.related?.length > 0 && (
        <section className="bg-[#F5F3EF] py-20">
          <div className="mx-auto max-w-[1280px] px-5 lg:px-8">
            <SectionHeading eyebrow="Keep Exploring" title="More Pack Brothers Destinations" className="mb-10" />
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {p.related.map((r) => <PropertyCard key={r.id} p={r} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
